// Project Overview tab — the default landing inside /projects/:id.
//
// Layout (commonsku-style):
//   - Main column (left, ~2/3): Activity feed — chronological list of
//     events written against this project (project.created, presentation
//     sent, order moved to client_approved, PO sent, etc.)
//   - Right column (~1/3): Project Info card — pinned summary metadata
//     (rep, contact, dates, budget, event type, notes).
//
// Data comes from `/projects/:id/detail` which the parent layout already
// fetched. We read it via React Router's <Outlet context={...}/>.
import { OrderContactsCard } from "@/components/proofs/OrderContactsCard";
import { Link, useOutletContext } from "react-router-dom";
import {
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Tag,
  User,
  Plus,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  deriveProjectNextStep,
  type ProjectNextStep,
  type NextStepAction,
} from "@/lib/projectNextStep";
import {
  useCreatePresentation,
  createPresentationErrorMessage,
} from "@/lib/useCreatePresentation";
import { ProjectTrackingCard } from "@/components/projects/ProjectTrackingCard";
import { ProjectActivityFeed } from "@/components/projects/ProjectActivityFeed";
import { ProjectFollowUps } from "@/components/projects/ProjectFollowUps";
import { tabsKey, type ProjectDetailResponse } from "@/pages/ProjectDetailPage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

/** Order rep on the opportunity (Stephanie 2026-10-16: "the order rep isn't
 *  being assigned"). Starts as the client rep; whoever handles the order can
 *  change it. Shows on the Production Report. Doesn't split commission —
 *  Finance picks a split on the commission screen. */
function OrderRepPicker({ projectId, clientRepId, orderRepId }: { projectId: string; clientRepId: string | null; orderRepId: string | null }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const reps = useQuery({
    queryKey: ["order-reps"],
    queryFn: () => api<{ reps: Array<{ id: string; name: string }> }>("/order-reps"),
    staleTime: 5 * 60 * 1000,
  });
  const save = useMutation({
    mutationFn: (id: string) => api(`/projects/${projectId}/order-rep`, { method: "POST", body: JSON.stringify({ order_rep_id: id }) }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: [tabsKey, projectId, "detail"] });
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(b?.error ?? "Not saved — try again.");
    },
  });
  const shown = save.isPending ? save.variables : (orderRepId ?? clientRepId ?? "");
  return (
    <div>
      <label htmlFor="order-rep" className="text-xs text-muted">Order rep</label>
      <select
        id="order-rep"
        value={shown}
        disabled={save.isPending || reps.isLoading}
        onChange={(e) => e.target.value && save.mutate(e.target.value)}
        className="mt-1 h-10 w-full rounded-md border border-rule bg-white px-2 text-sm text-ink"
      >
        {!shown && <option value="">Pick the order rep</option>}
        {(reps.data?.reps ?? []).map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
            {r.id === clientRepId ? " (client rep)" : ""}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-muted">Who handles this order — shows on the Production Report.</p>
      {error && <p role="alert" className="mt-1 text-xs text-risk">{error}</p>}
    </div>
  );
}

/** Who can set the order rep — the people who run orders. */
const ORDER_REP_SETTERS = ["admin", "client_rep", "order_rep", "production_rep"];

/** Change the project's in-hands date (Stephanie 2026-11-02: "there is no
 *  place to edit this information"). Quotes keep their own date (quote Edit). */
function InHandsEdit({ projectId, value, shown }: { projectId: string; value: string; shown: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (d: string) => api(`/projects/${projectId}`, { method: "PATCH", body: JSON.stringify({ in_hands_date: d }) }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: [tabsKey] });
      void qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: (e) => setError(e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? "We couldn't save the date. Try again.") : "We couldn't save the date. Try again."),
  });
  if (draft === null) {
    return (
      <span className="inline-flex items-center gap-2">
        <span>{shown}</span>
        <button type="button" onClick={() => { setError(null); setDraft(/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ""); }} className="text-xs font-medium text-muted hover:text-ink">
          Edit
        </button>
      </span>
    );
  }
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(draft)) { setError("Pick a date."); return; }
        save.mutate(draft);
      }}
    >
      <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus aria-label="In-hands date" className="h-8 rounded-md border border-rule bg-white px-2 text-sm text-ink" />
      <button type="submit" disabled={save.isPending} className="rounded-md bg-ink px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50">{save.isPending ? "Saving…" : "Save"}</button>
      <button type="button" onClick={() => setDraft(null)} className="px-1 text-xs text-ink">Cancel</button>
      {error && <span className="w-full text-xs text-rust-600">{error}</span>}
    </form>
  );
}

export function ProjectOverviewTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const { user } = useAuth();
  const canSetOrderRep = !!user && user.roles.some((r) => ORDER_REP_SETTERS.includes(r));
  const {
    project,
    primaryContact,
    clientRep,
    orderRep,
  } = data;

  const inHandsDate = (() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(project.inHandsDate)) {
      return project.inHandsDate;
    }
    return new Date(project.inHandsDate + "T00:00:00").toLocaleDateString(
      undefined,
      { year: "numeric", month: "short", day: "numeric" },
    );
  })();

  // Stage-aware "Next step" pointer. Reads the cancellation-excluding stage-node
  // doc counts the tabs use (data.stages.find(…)?.count) — NOT legacy counts.*,
  // which include cancelled rows — and the SAME shared create gate the tabs +
  // server rule engine consume. One compact block above the Activity feed.
  const stageCount = (key: string): number =>
    data.stages.find((s) => s.key === key)?.count ?? 0;
  const nextStep = deriveProjectNextStep({
    projectId: project.id,
    statusStage: project.statusStage,
    presentationCount: stageCount("presentation"),
    estimateCount: stageCount("estimate"),
    salesOrderCount: stageCount("sales_order"),
    invoiceCount: stageCount("invoice"),
  });

  // Tracking rollup — folded into Overview per CEO 2026-07-29 (the standalone
  // Tracking tab is gone). Omit-when-empty house rule: the card only earns
  // space once the project has at least one PO (production has started). Before
  // that there's nothing to track, so no empty shell renders. The PO count is
  // the same cancellation-excluding stage-node count the delete-confirm body
  // reads ("production").
  const hasPurchaseOrders = stageCount("production") > 0;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      {/* Main column — Next step pointer, the project's documents, activity */}
      <section className="min-w-0 space-y-6">
        <NextStepBlock step={nextStep} projectId={project.id} />
        <ProjectDocuments stages={data.stages} />
        <OrderContactsCard projectId={project.id} />
        {hasPurchaseOrders && <ProjectTrackingCard projectId={project.id} />}
        <ProjectFollowUps projectId={project.id} />
        <ProjectActivityFeed projectId={project.id} />
      </section>

      {/* Right column — project info card. self-start so the rail hugs its
          content instead of stretching to a half-empty box when the main column
          runs tall (the Tracking card can add height) — same fix the sub-nav
          aside uses in ProjectDetailPage. */}
      <aside className="rounded-md border border-rule bg-white p-5 sm:p-[22px] xl:self-start">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Project info
        </h2>

        <dl className="mt-4 flex flex-col gap-4 text-sm">
          <InfoRow icon={Calendar} label="In hands">
            <InHandsEdit projectId={project.id} value={project.inHandsDate} shown={inHandsDate} />
          </InfoRow>
          {project.budgetAmount && (
            <InfoRow icon={DollarSign} label="Budget">
              {Number(project.budgetAmount).toLocaleString(undefined, {
                style: "currency",
                currency: project.budgetCurrency ?? "USD",
              })}
            </InfoRow>
          )}
          {project.eventType && (
            <InfoRow icon={Tag} label="Event type">
              {project.eventType}
            </InfoRow>
          )}
          {clientRep && (
            <InfoRow icon={User} label="Client rep">
              <div>
                <div className="text-ink">{clientRep.fullName}</div>
                <div className="text-xs text-muted">{clientRep.email}</div>
              </div>
            </InfoRow>
          )}
          {canSetOrderRep ? (
            <OrderRepPicker projectId={project.id} clientRepId={clientRep?.id ?? null} orderRepId={orderRep?.id ?? null} />
          ) : orderRep && orderRep.id !== clientRep?.id && (
            <InfoRow icon={User} label="Order rep">
              <div>
                <div className="text-ink">{orderRep.fullName}</div>
                <div className="text-xs text-muted">{orderRep.email}</div>
              </div>
            </InfoRow>
          )}
          {primaryContact && (
            <div>
              <div className="text-xs text-muted">Initial contact</div>
              <div className="mt-1 text-brown-500">
                {[primaryContact.firstName, primaryContact.lastName]
                  .filter(Boolean)
                  .join(" ")}
              </div>
              {primaryContact.title && (
                <div className="text-xs text-muted">
                  {primaryContact.title}
                </div>
              )}
              <div className="mt-2 flex flex-col gap-1 text-xs">
                {primaryContact.email && (
                  <a
                    href={`mailto:${primaryContact.email}`}
                    className="flex items-center gap-1.5 text-muted hover:text-ink"
                  >
                    <Mail className="h-3 w-3" />
                    {primaryContact.email}
                  </a>
                )}
                {primaryContact.phone && (
                  <a
                    href={`tel:${primaryContact.phone}`}
                    className="flex items-center gap-1.5 text-muted hover:text-ink"
                  >
                    <Phone className="h-3 w-3" />
                    {primaryContact.phone}
                  </a>
                )}
              </div>
            </div>
          )}
          {project.notes && (
            <div>
              <div className="text-xs text-muted">Notes</div>
              <div className="mt-1 whitespace-pre-wrap text-ink">
                {project.notes}
              </div>
            </div>
          )}
        </dl>
      </aside>
    </div>
  );
}

// ─── Next step ──────────────────────────────────────────────────────────────
//
// One compact, stage-aware pointer at the top of the Overview so a rep landing
// on a fresh project is never stranded (CEO 2026-07-29). The step is derived by
// the pure deriveProjectNextStep (bound to the shared create gate); this
// component only renders it and wires the actions to the EXISTING create
// machinery — the shared useCreatePresentation mutation for create_presentation,
// and a navigation into each tab's own create/detail affordance for the rest.
// No creation logic is duplicated here. House one-primary rule: one primary +
// at most one secondary.
function NextStepBlock({
  step,
  projectId,
}: {
  step: ProjectNextStep;
  projectId: string;
}) {
  // Same shared mutation the Presentation tab's "New presentation" button fires
  // (POST /presentations → invalidate → land in the in-project builder). Reused,
  // not reimplemented.
  const createPresentation = useCreatePresentation(projectId);
  const createError = createPresentationErrorMessage(createPresentation.error);

  if (!step.primary) {
    // Terminal project (closed / lost) — a quiet, non-actionable line; the header
    // owns Reopen, so the Overview never offers a stale create.
    if (step.terminalNote) {
      return (
        <div className="rounded-md border border-rule bg-white px-5 py-4 text-sm text-muted">
          {step.terminalNote}
        </div>
      );
    }
    return null;
  }

  return (
    <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
      <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
        Next step
      </h2>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <NextStepButton
          action={step.primary}
          isPrimary
          createPresentation={createPresentation}
        />
        {step.secondary && (
          <NextStepButton
            action={step.secondary}
            isPrimary={false}
            createPresentation={createPresentation}
          />
        )}
        {step.tertiary && (
          <NextStepButton
            action={step.tertiary}
            isPrimary={false}
            createPresentation={createPresentation}
          />
        )}
      </div>
      {createError && <p className="mt-2 text-xs text-rust-600">{createError}</p>}
    </section>
  );
}

function NextStepButton({
  action,
  isPrimary,
  createPresentation,
}: {
  action: NextStepAction;
  isPrimary: boolean;
  createPresentation: ReturnType<typeof useCreatePresentation>;
}) {
  const variant = isPrimary ? "primary" : "outline";
  const size = "md" as const;

  // create_presentation is the ONLY step that mutates rather than navigates — it
  // fires the shared mutation and lands the rep in the new builder. Every other
  // step (create_estimate + view_*) is a navigation into the tab that already
  // owns that affordance, so it renders as an <a>/Link (no duplicated logic).
  if (action.kind === "create_presentation") {
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => createPresentation.mutate()}
        disabled={createPresentation.isPending}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {createPresentation.isPending ? "Creating…" : action.label}
      </Button>
    );
  }

  const isCreate = action.kind === "create_estimate" || action.kind === "create_sales_order";
  return (
    <Button asChild variant={variant} size={size}>
      <Link to={action.href ?? "#"}>
        {isCreate ? (
          <Plus className="h-4 w-4" aria-hidden />
        ) : (
          <ArrowRight className="h-4 w-4" aria-hidden />
        )}
        {action.label}
      </Link>
    </Button>
  );
}

// ─── Documents on this project ──────────────────────────────────────────────
//
// Handoff W08: "One project connects presentations, estimates, orders and
// production. Separate project stage from document/payment status." The
// project's stage is in the header; this lists the documents it holds — the
// cancellation-excluding stage-node counts the tabs already read — each
// linking to its tab. Not a progress rail (that was removed 2026-07-26): a
// plain list of what exists.
const DOC_LABEL: Record<string, string> = {
  presentation: "Presentations",
  // CEO 2026-10-15: quotes still being worked on vs quotes the client approved.
  estimate: "Quotes in progress",
  sales_order: "Approved quotes",
  production: "Purchase orders",
  invoice: "Invoices",
};

function ProjectDocuments({ stages }: { stages: ProjectDetailResponse["stages"] }) {
  if (stages.length === 0) return null;
  return (
    <div className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
      <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Documents on this project</h2>
      <ul className="mt-3 grid gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-5">
        {stages.map((st) => (
          <li key={st.key} className="bg-white">
            <Link
              to={st.href}
              className="flex h-full items-baseline justify-between gap-2 px-3.5 py-3 hover:bg-[#fafaf8] sm:flex-col sm:items-start"
            >
              <span className="text-xs text-muted">{DOC_LABEL[st.key] ?? st.key}</span>
              <span className={st.count > 0 ? "text-lg font-medium tabular-nums text-ink" : "text-sm text-muted"}>
                {st.count > 0 ? st.count.toLocaleString() : st.status === "skipped" ? "Skipped" : "None yet"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface InfoRowProps {
  icon: typeof Mail;
  label: string;
  children: React.ReactNode;
}

function InfoRow({ icon: Icon, label, children }: InfoRowProps) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <Icon className="h-3 w-3" aria-hidden />
        {label}
      </div>
      <div className="mt-1 text-ink">{children}</div>
    </div>
  );
}
